import { useCallback, useState } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { CircleCheckBig, Trash2 } from 'lucide-react';

import { SensitiveField } from '@/components/SensitiveField';
import { Button } from '@/components/ui/button.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { cn } from '@/lib/utils';
import {
  CARRIER_TYPE,
  type CarrierType,
  type UnverifiedInsurance,
  type VerifiedInsurance,
  prettyTypeName,
} from '@/types/insurance.type.ts';
import type { Patient } from '@/types/patient-info.type.ts';

const insuranceCardVariants = cva('', {
  variants: {
    variant: {
      default: 'bg-hydrangea-50 border-hydrangea-500 [&_[data-slot=insurance-badge]]:bg-gray-300',
      orchid: 'bg-orchid-100 border-orchid-500 [&_[data-slot=insurance-badge]]:bg-orchid-500',
      marigold:
        'bg-marigold-50 border-marigold-500 [&_[data-slot=insurance-badge]]:bg-marigold-500',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type CardColor = VariantProps<typeof insuranceCardVariants>['variant'];
const TYPE_VARIANTS: Record<CarrierType, CardColor> = {
  [CARRIER_TYPE.MEDICARE]: 'orchid',
  [CARRIER_TYPE.MEDICARE_ADVANTAGE]: 'orchid',
  [CARRIER_TYPE.MEDICAID]: 'marigold',
  [CARRIER_TYPE.MEDICAID_MANAGED_CARE]: 'marigold',
};

interface Props {
  // TODO Whenever insurance types are unified this should just be one type
  //  Remove `InsuranceInfo`
  insurance: VerifiedInsurance | UnverifiedInsurance;
  policyHolder: Patient;
  onRemove?: () => void;
}

export function InsuranceCard({ insurance, policyHolder, onRemove }: Props) {
  const [isRemoving, setIsRemoving] = useState(false);
  const promptRemove = useCallback(() => {
    setIsRemoving(true);
  }, []);

  const closeAndRemove = useCallback(() => {
    setIsRemoving(false);
    onRemove?.();
  }, [onRemove]);

  return (
    <>
      <div className="relative sm:w-[322px]">
        <InsuranceCardVisual insurance={insurance} policyHolder={policyHolder} />
        {onRemove && (
          <Button
            variant="outline"
            size="sm"
            onClick={promptRemove}
            className="absolute right-4 bottom-3 bg-transparent"
          >
            <Trash2 className="size-4" /> Remove
          </Button>
        )}
      </div>

      {isRemoving && onRemove && (
        <Dialog open={isRemoving} onOpenChange={setIsRemoving}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>Are you sure you want to remove this plan?</DialogTitle>
            </DialogHeader>

            <InsuranceCardVisual insurance={insurance} policyHolder={policyHolder} />

            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <Button variant="outline" onClick={() => setIsRemoving(false)}>
                No
              </Button>
              <Button onClick={closeAndRemove}>Yes, remove</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

const InsuranceCardVisual = ({
  insurance,
  policyHolder,
}: {
  insurance: VerifiedInsurance | UnverifiedInsurance;
  policyHolder: Patient;
}) => {
  const carrierType = insurance.carrier.carrierType;
  const name = insuranceName(insurance);
  const memberId = insurance.memberId;
  const verified = isVerified(insurance);
  const variant = TYPE_VARIANTS[carrierType] || 'default';

  return (
    <div
      data-slot="insurance-card"
      className={cn(
        'space-y-4 rounded-[12px] border px-4 py-3 sm:w-[322px]',
        insuranceCardVariants({ variant })
      )}
    >
      <div className="grid grid-cols-[1fr_auto] gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-lg leading-none font-bold">{prettyTypeName(carrierType)}</p>
          <p className="text-sm leading-none font-normal">{name}</p>
        </div>

        {verified ? (
          <CircleCheckBig className="size-6 shrink-0 justify-self-end text-foreground" />
        ) : (
          <InsuranceCardBadge className="justify-self-end" />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-xs leading-none font-normal text-charcoal-400">Policy Holder</p>
        <SensitiveField asChild>
          <p className="text-sm leading-none">
            {policyHolder.firstName} {policyHolder.lastName}
          </p>
        </SensitiveField>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-xs leading-none font-normal text-charcoal-400">Member ID</p>
        <SensitiveField asChild>
          <p className="text-sm leading-none">{memberId}</p>
        </SensitiveField>
      </div>
    </div>
  );
};

function insuranceName(insurance: VerifiedInsurance | UnverifiedInsurance): string {
  return 'displayName' in insurance.carrier
    ? insurance.carrier.displayName
    : insurance.carrier.name;
}

function isVerified(insurance: VerifiedInsurance | UnverifiedInsurance): boolean {
  return !('uuid' in insurance.carrier);
}

function InsuranceCardBadge({ className }: { className: string }) {
  return (
    <div
      data-slot="insurance-badge"
      className={cn('flex h-8 shrink-0 items-center rounded-[20px] px-3', className)}
    >
      <p className="text-sm leading-normal text-main uppercase">Unverified</p>
    </div>
  );
}
